import React from 'react';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Stack from '@mui/material/Stack';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockListHeader from '../components/GuideMockListHeader';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideMemberBulkRegister: React.FC = () => {
  return (
    <GuidePageLayout
      title="要員を一括登録する"
      description="CSVファイルを使用して複数の要員を一括で登録します。"
      breadcrumbs={[{ label: '要員管理', path: '/guide/member/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
            ['事前準備', 'パートナーやユーザーが登録済みであること（IDを使用するため）'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順1: CSVテンプレートをダウンロードする">
        <GuideStepList
          steps={[
            { label: '要員管理画面を開く' },
            {
              label: 'リストヘッダーの「CSVテンプレートダウンロード」アイコンをクリックする',
              description: '追加ボタン（+）の右隣にあるダウンロードアイコンをクリックします。',
              content: (
                <GuideMockContainer label="リストヘッダーのアイコン">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ pointerEvents: 'none' }}
                  >
                    <GuideMockListHeader createLabel="要員新規作成" />
                    <UploadFileIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <FileDownloadIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: 'ダイアログで入力値の対応表を確認する',
              description:
                '雇用形態ID、性別ID、契約形態ID、個人事業主フラグなどマスタ区分・定数のコード値と表示名の対応表が表示されます。ユーザーIDやパートナーIDのような画面からコピーできるID項目は対応表には含まれません。',
            },
            {
              label: '「CSVテンプレートをダウンロード」ボタンをクリックしてテンプレートを取得する',
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="手順2: CSVファイルを作成する">
        <GuideStepList
          steps={[
            { label: 'ダウンロードしたテンプレートCSVをExcel等で開く' },
            {
              label: '各列にデータを入力する',
              description:
                '必須列には必ず値を入力してください。IDの列には対応する情報の詳細画面タイトル横のコピーボタンで取得した値を入力します。',
            },
            { label: 'UTF-8形式でCSVとして保存する' },
          ]}
        />
      </GuideSection>

      <GuideSection title="手順3: 一括登録を実行する">
        <GuideStepList
          steps={[
            {
              label: 'リストヘッダーの「一括登録」アイコンをクリックする',
              description:
                '追加ボタン（+）と「CSVテンプレートダウンロード」の間にあるアップロードアイコンをクリックします。',
            },
            { label: 'CSVファイルを選択し「読み込み」ボタンを押す' },
            {
              label: 'プレビュー画面で読み込み結果を確認する',
              description:
                '各行のステータスが表示されます。エラーがある場合は対象セルが赤くなり、エラー内容がアイコンのツールチップで確認できます。',
            },
            {
              label: '全行が成功であれば「一括登録」ボタンを押す',
              description:
                'エラーがある行がある場合、一括登録ボタンは非活性です。CSVを修正して再度読み込んでください。',
            },
            {
              label: '登録処理が完了するまで待つ',
              description:
                '1件ずつ登録されます。各行の状態がサーキュラー→成功/失敗アイコンに変わります。',
            },
            {
              label:
                '全て成功なら「完了」ボタンを押す。失敗がある場合は「再実行」で失敗行を再処理する',
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="CSVカラム一覧">
        <GuideTable
          headers={['ヘッダー名', '必須', '形式', '備考']}
          rows={[
            ['要員名', '必須', 'テキスト（最大300文字）', ''],
            ['要員名（カナ）', '必須', 'テキスト（最大300文字）', ''],
            ['雇用形態ID', '必須', 'コード値', 'EMPLOYEE / 社外コード'],
            ['ユーザーID', '任意', 'ID', '社員の場合に設定'],
            ['性別ID', '必須', 'コード値', 'マスタの性別コード'],
            ['個人事業主フラグ', '必須', 'true/false', '社員の場合はfalse'],
            ['所属', '任意', 'テキスト（最大300文字）', '社員の場合に設定'],
            ['パートナーID', '条件付き必須', 'ID（最大64文字）', '社外の場合に必須'],
            ['契約形態ID', '条件付き必須', 'コード値', '社外の場合に必須'],
            ['備考', '任意', 'テキスト（最大10000文字）', ''],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '表示条件', '活性条件', '動作']}
          rows={[
            [
              'CSVテンプレートダウンロード（アイコン）',
              '常に表示',
              '常に活性',
              'テンプレートダイアログを開く',
            ],
            ['一括登録（アイコン）', '常に表示', '常に活性', 'アップロードダイアログを開く'],
            [
              '読み込みボタン',
              'アップロード画面',
              'ファイル選択時',
              'CSVをパースしてプレビュー表示',
            ],
            ['一括登録ボタン', 'プレビュー画面', '全行読み込み成功時', '1件ずつ登録APIを実行'],
            [
              'キャンセルボタン',
              'プレビュー画面',
              '常に活性',
              'データを破棄してアップロード画面に戻る',
            ],
            ['完了ボタン', '全件登録成功時', '常に活性', 'ダイアログを閉じて一覧を更新'],
            ['再実行ボタン', '登録失敗行がある場合', '常に活性', '失敗行のみ再度登録を試行'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>
            IDの列（ユーザーID、パートナーID）は各情報の詳細画面タイトル横のコピーボタンから取得してください。
          </GuideInfoBox>
          <GuideInfoBox>
            複数選択の項目がある場合は、カンマ（,）区切りで入力してください。
          </GuideInfoBox>
          <GuideInfoBox type="warning">
            一括登録中にダイアログを閉じることはできません。登録処理が完了するまでお待ちください。
          </GuideInfoBox>
          <GuideInfoBox type="warning">
            コード値が不正な場合はセルが赤く表示されます。CSVを修正して再度読み込んでください。
          </GuideInfoBox>
          <GuideInfoBox>
            社外要員の場合、パートナーIDと契約形態IDは必須です。雇用形態がEMPLOYEE以外の行で未入力の場合はエラーになります。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideMemberBulkRegister;
